import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import Navbar from "../../feature/Navbar";
import Footer from "../../feature/Footer";
import WhatsAppButton from "../../feature/WhatsAppButton";
import { ProductCard } from "../../components/base/ProductCard";

const isVisible = (p: any) => {
  const v = p?.visible;
  return v === true || v === 1 || v === "true" || v === "1";
};

export default function CategoryPage() {
  const { slug } = useParams();
  const [category, setCategory] = useState<any>(null);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }

      try {
        const [{ data: categoryData }, { data: allCats }] = await Promise.all([
          supabase.from("categories").select("*").eq("slug", slug).maybeSingle(),
          supabase.from("categories").select("*").order("created_at"),
        ]);

        setCategory(categoryData || null);
        setAllCategories(allCats || []);

        if (!categoryData) {
          setProducts([]);
          setLoading(false);
          return;
        }

        // Busca única usando .contains() — funciona tanto para arrays JSON quanto text[]
        const { data: productsData, error: productsError } = await supabase
          .from("ray_melo_products")
          .select("*")
          .contains("categories", [slug])
          .order("created_at", { ascending: false });

        if (productsError) throw productsError;

        // Deduplica por id e filtra visibilidade
        const seen = new Set<string>();
        const unique = (productsData || [])
          .filter(isVisible)
          .filter((item: any) => {
            if (seen.has(String(item.id))) return false;
            seen.add(String(item.id));
            return true;
          });

        setProducts(unique);
      } catch (error) {
        console.error("Erro ao buscar categoria:", error);
        setCategory(null);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-black/20 border-t-black rounded-full animate-spin" />
            <p className="text-[11px] tracking-[0.2em] text-black/40 uppercase">Carregando...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      <div className="relative bg-[#1A1A1A] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=elegant%20Brazilian%20fashion%20boutique%20collection%20flat%20lay%20clothing%20arrangement%2C%20dark%20moody%20background%2C%20editorial%20fashion%20photography%2C%20sophisticated%20minimalist%20style%2C%20warm%20tones&width=1440&height=220&seq=category-hero&orientation=landscape"
          alt={category?.name || "Categoria"}
          className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="flex items-center gap-2 text-[11px] text-white/50 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Início</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/catalogo" className="hover:text-white transition-colors">Catálogo</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-white/80">{category?.name || "Categoria"}</span>
          </div>
          <h1
            className="text-4xl md:text-5xl font-light text-white tracking-[0.05em]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {category?.name || "Categoria"}
          </h1>
          <p className="text-[12px] text-white/50 mt-2 tracking-wide">
            {products.length} {products.length === 1 ? "peça encontrada" : "peças encontradas"}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10">
        <div className="flex gap-8">
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-24">
              <p className="text-[10px] tracking-[0.25em] font-semibold text-black uppercase mb-3">
                Categorias
              </p>
              <ul className="space-y-0.5">
                <li>
                  <Link
                    to="/catalogo"
                    className="w-full text-left px-3 py-2.5 text-[12px] tracking-[0.05em] transition-colors flex items-center justify-between text-black/50 hover:text-black hover:bg-black/5"
                  >
                    <span>Todas as peças</span>
                  </Link>
                </li>
                {allCategories.map((cat) => {
                  const active = cat.slug === slug;
                  return (
                    <li key={cat.id}>
                      <Link
                        to={`/categoria/${cat.slug}`}
                        className={`w-full text-left px-3 py-2.5 text-[12px] tracking-[0.05em] transition-colors flex items-center justify-between ${
                          active
                            ? "bg-black text-white"
                            : "text-black/60 hover:text-black hover:bg-black/5"
                        }`}
                      >
                        <span>{cat.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="lg:hidden flex gap-2 overflow-x-auto pb-3 mb-5 [scrollbar-width:none]">
              <Link
                to="/catalogo"
                className="flex-shrink-0 px-4 h-9 border border-black/15 text-[11px] tracking-[0.1em] uppercase text-black/60 hover:border-black/40 hover:text-black transition-colors whitespace-nowrap flex items-center"
              >
                Todas
              </Link>
              {allCategories.map((cat) => {
                const active = cat.slug === slug;
                return (
                  <Link
                    key={cat.id}
                    to={`/categoria/${cat.slug}`}
                    className={`flex-shrink-0 px-4 h-9 border text-[11px] tracking-[0.1em] uppercase transition-colors whitespace-nowrap flex items-center ${
                      active
                        ? "bg-black text-white border-black"
                        : "border-black/15 text-black/60 hover:border-black/40 hover:text-black"
                    }`}
                  >
                    {cat.name}
                  </Link>
                );
              })}
            </div>

            {products.length === 0 ? (
              <div className="py-24 text-center">
                <i className="ri-shopping-bag-line text-5xl text-black/15 mb-5 block" />
                <p className="text-black/40 uppercase text-[11px] tracking-[0.25em] mb-6">
                  Nenhuma peça encontrada nesta categoria
                </p>
                <Link
                  to="/catalogo"
                  className="inline-block border border-black/25 px-8 py-3 text-[11px] tracking-[0.18em] uppercase hover:bg-black hover:text-white transition-all duration-300 whitespace-nowrap"
                >
                  Ver Catálogo Completo
                </Link>
              </div>
            ) : (
              <div
                className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5"
                data-product-shop
              >
                {products.map((product, index) => (
                  <ProductCard
                    key={`product-${product.id}-${index}`}
                    product={product}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}